import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { initTheme } from "@/lib/theme";

initTheme();

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import DmsShell from "@/pages/dms/DmsShell";
import DmsDashboardRouter from "@/pages/dms/DmsDashboardRouter";
import CollectionsPage from "@/pages/dms/CollectionsPage";
import {
  CategoriesPage, ProductsPage, InvoiceNumberingPage, DistributorsPage, DistributorDetailPage, RetailersPage,
  OwnerPrimaryOrdersPage, OwnerOrderDetailPage, OwnerCreateOrderPage, OwnerInventoryPage, OwnerDistributorInventoryPage, PrimaryLedgerPage, RewardWalletsPage, OwnerReturnsPage,
} from "@/pages/dms/OwnerPages";
import {
  DistributorBrowsePage, DistributorPriceListPage, DistributorOrdersPage, DistributorOrderDetailPage, DistributorStockPage, DistributorReturnsPage,
  DistributorSalespersonsPage, DistributorStockAlertsPage,
} from "@/pages/dms/DistributorPages";
import {
  DistRetailersPage, DistRetailerDetailPage, DistSecondaryOrdersPage, DistSecondaryOrderDetailPage, SecondaryLedgerPage,
} from "@/pages/dms/DistributorSecondaryPages";
import {
  OwnerMyTransactionsPage, OwnerAllTransactionsPage, DistributorMyTransactionsPage, RetailerMyTransactionsPage,
} from "@/pages/dms/TransactionsPages";
import {
  RetailerBrowsePage, RetailerOrdersPage, RetailerOrderDetailPage,
} from "@/pages/dms/RetailerPages";
import {
  SpDistributorsPage, SpRetailersPage, SpNewRetailerPage, SpNewOrderPage,
  SpOrdersPage, SpCollectPaymentPage, SpCollectionsPage, SpEditRetailerPage, SpOutstandingPage, OrderValueDetailPage,
  TlDistributorsPage, OwnerSalesAssignmentsPage,
} from "@/pages/dms/SalesTeamPages";
import { SuperAdminUsersPage } from "@/pages/dms/SuperAdminPages";
import { OwnerUsersPage } from "@/pages/dms/OwnerUsersPage";
import { ActivityLogPage } from "@/pages/dms/ActivityLogPage";
import { BackupPage } from "@/pages/dms/BackupPage";
import { TicketsPage } from "@/pages/dms/TicketsPage";
import { LiveTrackingPage } from "@/pages/dms/LiveTrackingPage";
import { TlDashboardPage, TlDistributorsMonitoringPage, TlSalespersonsPage, TlOrdersMonitoringPage, TlRetailersPage, TlProductsPage, TlStockAlertsPage, TlOutstandingPage, TlOutstandingDetailPage } from "@/pages/dms/TeamLeaderPages";
import { OwnerTlPerformancePage, OwnerDistributorSalesListPage, OwnerDistributorSalesDetailPage } from "@/pages/dms/OwnerInsightsPages";
import { OwnerHierarchyPage } from "@/pages/dms/HierarchyPage";
import { RmDashboardPage, RmTeamLeadersPage, RmRegionPerformancePage, RmDistributorsPage, RmSalespersonsPage, RmProductsPage, RmOrdersMonitoringPage, RmOutstandingPage, RmFulfillmentPage, RmOutstandingDetailPage } from "@/pages/dms/RegionalManagerPages";
import {
  OwnerCouponsPage,
  OwnerCouponBatchDetailPage,
  OwnerCouponsListPage,
  OwnerRedemptionsPage,
  OwnerCreditNotesPage,
  OwnerDispatchAdvicesPage,
  OwnerCouponReportsPage,
  OwnerCouponAuditLogPage,
  OwnerFraudDashboardPage,
  OwnerBoxesPage,
  OwnerDeletedItemsPage,
  SalesOfficerScanPage,
  RetailerScanPage,
  RetailerWalletPage,
  DistributorCouponsPage,
  DistributorScanPage,
  OwnerScanAuditPage,
} from "@/pages/dms/CouponsV2";
import { PrintEbillPage, PrintRetailerBillPage, PrintPurchaseOrderPage, PrintDocumentPage, PrintChallanPage } from "@/pages/dms/PrintPages";
import { ImportExportPage, DirectSalesPage, DocumentsPage, MyBankPage, MyProfilePage } from "@/pages/dms/Phase2CPages";
import { ReportsHubPage, GenericReportPage } from "@/pages/dms/ReportsPages";
import { PriceCircularsPage, PriceCircularDetailPage, NewPriceCircularPage, SettingsPage } from "@/pages/dms/PriceCircularPages";
import { ExpensesPage } from "@/pages/dms/ExpensesPage";
import { AttendancePage, RmRetailersPage, RetailerAccessPage } from "@/pages/dms/AttendancePages";
import { HrAttendancePage, HrPerformancePage, HrSalaryMasterPage } from "@/pages/dms/HrPages";
import { DistributorSaleLitersPage } from "@/pages/dms/DistributorPages";
import FieldLocationGuard from "@/pages/dms/FieldLocationGuard";
import AppUpdatePrompt from "@/components/AppUpdatePrompt";
import DayPlanGate, { PlanVsActualPage, SpTargetsPage } from "@/pages/dms/DayPlanPages";
import { HrPlanActualPage } from "@/pages/dms/HrPlanActualPage";
import { BankAccountsPage, BankTransactionsPage, CashRegisterPage, ChequesPage, LoanAccountsPage } from "@/pages/dms/CashBankPages";
import { GodownsPage, StockTransfersPage } from "@/pages/dms/WarehousePages";
import { TlSaleLitersPage, RmSaleLitersPage, SpSaleLitersPage } from "./pages/dms/SaleLitersShared";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-sm text-slate-500">Loading…</div></div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function DmsPage({ Component }) {
  return (
    <Protected>
      <>
      <FieldLocationGuard />
      <DayPlanGate />
      <DmsShell>
        <Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading…</div>}>
          <Component />
        </Suspense>
      </DmsShell>
      </>
    </Protected>
  );
}

// Print pages get a bare protected wrapper (no shell)
function PrintPage({ Component }) {
  return (
    <Protected>
      <Component />
    </Protected>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/dms/owner/collections" element={<DmsPage Component={CollectionsPage} />} />

      {/* Dashboard router */}
      <Route path="/dms" element={<DmsPage Component={DmsDashboardRouter} />} />

      {/* Owner */}
      <Route path="/dms/owner/categories"          element={<DmsPage Component={CategoriesPage} />} />
      <Route path="/dms/owner/products"            element={<DmsPage Component={ProductsPage} />} />
      <Route path="/dms/owner/invoice-numbering"    element={<DmsPage Component={InvoiceNumberingPage} />} />
      <Route path="/dms/owner/distributors"        element={<DmsPage Component={DistributorsPage} />} />
      <Route path="/dms/owner/distributors/:id"    element={<DmsPage Component={DistributorDetailPage} />} />
      <Route path="/dms/owner/retailers"            element={<DmsPage Component={RetailersPage} />} />
      <Route path="/dms/owner/primary-orders"      element={<DmsPage Component={OwnerPrimaryOrdersPage} />} />
      <Route path="/dms/owner/primary-orders/create" element={<DmsPage Component={OwnerCreateOrderPage} />} />
      <Route path="/dms/owner/primary-orders/:id"  element={<DmsPage Component={OwnerOrderDetailPage} />} />
      <Route path="/dms/owner/returns"             element={<DmsPage Component={OwnerReturnsPage} />} />
      <Route path="/dms/owner/inventory"           element={<DmsPage Component={OwnerInventoryPage} />} />
      <Route path="/dms/owner/distributor-inventory" element={<DmsPage Component={OwnerDistributorInventoryPage} />} />
      <Route path="/dms/owner/ledger"              element={<DmsPage Component={PrimaryLedgerPage} />} />
      <Route path="/dms/owner/my-transactions"      element={<DmsPage Component={OwnerMyTransactionsPage} />} />
      <Route path="/dms/owner/all-transactions"     element={<DmsPage Component={OwnerAllTransactionsPage} />} />
      <Route path="/dms/owner/reward-wallets"       element={<DmsPage Component={RewardWalletsPage} />} />
      <Route path="/dms/owner/retailer-prices"     element={<DmsPage Component={CategoriesPage} />} />
      <Route path="/dms/owner/users"               element={<DmsPage Component={OwnerUsersPage} />} />
      <Route path="/dms/owner/activity-log"        element={<DmsPage Component={ActivityLogPage} />} />
      <Route path="/dms/owner/backup"              element={<DmsPage Component={BackupPage} />} />
      <Route path="/dms/tickets"                   element={<DmsPage Component={TicketsPage} />} />
      <Route path="/dms/owner/hierarchy"           element={<DmsPage Component={OwnerHierarchyPage} />} />
      <Route path="/dms/owner/sales-assignments"   element={<DmsPage Component={OwnerSalesAssignmentsPage} />} />
      <Route path="/dms/owner/live-tracking"       element={<DmsPage Component={LiveTrackingPage} />} />
      <Route path="/dms/owner/tl-performance"      element={<DmsPage Component={OwnerTlPerformancePage} />} />
      <Route path="/dms/owner/distributor-sales"   element={<DmsPage Component={OwnerDistributorSalesListPage} />} />
      <Route path="/dms/owner/distributor-sales/:id" element={<DmsPage Component={OwnerDistributorSalesDetailPage} />} />
      <Route path="/dms/owner/coupons"                  element={<DmsPage Component={OwnerCouponsPage} />} />
      <Route path="/dms/owner/coupons/all"              element={<DmsPage Component={OwnerCouponsListPage} />} />
      <Route path="/dms/owner/coupons/batches/:bid"     element={<DmsPage Component={OwnerCouponBatchDetailPage} />} />
      <Route path="/dms/owner/coupons/redemptions"      element={<DmsPage Component={OwnerRedemptionsPage} />} />
      <Route path="/dms/owner/coupons/credit-notes"     element={<DmsPage Component={OwnerCreditNotesPage} />} />
      <Route path="/dms/owner/coupons/dispatch-advices" element={<DmsPage Component={OwnerDispatchAdvicesPage} />} />
      <Route path="/dms/owner/coupons/audit-log"        element={<DmsPage Component={OwnerCouponAuditLogPage} />} />
      <Route path="/dms/owner/coupons/fraud"            element={<DmsPage Component={OwnerFraudDashboardPage} />} />
      <Route path="/dms/owner/coupon-reports"           element={<DmsPage Component={OwnerCouponReportsPage} />} />
      <Route path="/dms/owner/boxes"                     element={<DmsPage Component={OwnerBoxesPage} />} />
      <Route path="/dms/owner/deleted-items"              element={<DmsPage Component={OwnerDeletedItemsPage} />} />
      <Route path="/dms/owner/price-circulars"     element={<DmsPage Component={PriceCircularsPage} />} />
      <Route path="/dms/owner/price-circulars/new" element={<DmsPage Component={NewPriceCircularPage} />} />
      <Route path="/dms/owner/price-circulars/:id" element={<DmsPage Component={PriceCircularDetailPage} />} />
      <Route path="/dms/owner/settings"            element={<DmsPage Component={SettingsPage} />} />
      <Route path="/dms/expenses"                  element={<DmsPage Component={ExpensesPage} />} />
      {/* Phase 2B — Cash & Bank */}
      <Route path="/dms/finance/bank-accounts"     element={<DmsPage Component={BankAccountsPage} />} />
      <Route path="/dms/finance/bank-transactions" element={<DmsPage Component={BankTransactionsPage} />} />
      <Route path="/dms/finance/cash-register"     element={<DmsPage Component={CashRegisterPage} />} />
      <Route path="/dms/finance/cheques"           element={<DmsPage Component={ChequesPage} />} />
      <Route path="/dms/finance/loans"             element={<DmsPage Component={LoanAccountsPage} />} />
      {/* Phase 2B — Warehouse */}
      <Route path="/dms/warehouse/godowns"         element={<DmsPage Component={GodownsPage} />} />
      <Route path="/dms/warehouse/transfers"       element={<DmsPage Component={StockTransfersPage} />} />
      {/* Phase 2C */}
      <Route path="/dms/import-export"             element={<DmsPage Component={ImportExportPage} />} />
      <Route path="/dms/direct-sales"              element={<DmsPage Component={DirectSalesPage} />} />
      <Route path="/dms/my-bank"                   element={<DmsPage Component={MyBankPage} />} />
      <Route path="/dms/my-profile" element={<DmsPage Component={MyProfilePage} />} />
      <Route path="/dms/documents"                 element={<DmsPage Component={DocumentsPage} />} />
      {/* Phase 3 — Reports */}
      <Route path="/dms/reports"                   element={<DmsPage Component={ReportsHubPage} />} />
      <Route path="/dms/reports/:reportId"         element={<DmsPage Component={GenericReportPage} />} />
      <Route path="/dms/salesperson/scan"          element={<DmsPage Component={SalesOfficerScanPage} />} />
      <Route path="/dms/retailer/wallet"           element={<DmsPage Component={RetailerWalletPage} />} />
      <Route path="/dms/retailer/scan"             element={<DmsPage Component={RetailerScanPage} />} />
      <Route path="/dms/distributor/coupons"       element={<DmsPage Component={DistributorCouponsPage} />} />
      <Route path="/dms/distributor/scan"          element={<DmsPage Component={DistributorScanPage} />} />
      <Route path="/dms/owner/coupons/scan-audit"  element={<DmsPage Component={OwnerScanAuditPage} />} />
      <Route path="/dms/team-leader/live-tracking" element={<DmsPage Component={LiveTrackingPage} />} />
      <Route path="/dms/team-leader/salespersons"  element={<DmsPage Component={TlSalespersonsPage} />} />
      <Route path="/dms/team-leader/orders"        element={<DmsPage Component={TlOrdersMonitoringPage} />} />
      <Route path="/dms/team-leader/retailers"     element={<DmsPage Component={TlRetailersPage} />} />
      <Route path="/dms/team-leader/attendance"    element={<DmsPage Component={AttendancePage} />} />
      <Route path="/dms/team-leader/products"      element={<DmsPage Component={TlProductsPage} />} />
      <Route path="/dms/team-leader/collections"    element={<DmsPage Component={SpCollectionsPage} />} />
      <Route path="/dms/regional-manager/collections" element={<DmsPage Component={SpCollectionsPage} />} />
      <Route path="/dms/team-leader/order-value"      element={<DmsPage Component={OrderValueDetailPage} />} />
      <Route path="/dms/team-leader/sale-liters" element={<DmsPage Component={TlSaleLitersPage} />} />
      <Route path="/dms/regional-manager/order-value" element={<DmsPage Component={OrderValueDetailPage} />} />
      <Route path="/dms/regional-manager/sale-liters" element={<DmsPage Component={RmSaleLitersPage} />} />
      <Route path="/dms/regional-manager/orders" element={<DmsPage Component={RmOrdersMonitoringPage} />} />
      <Route path="/dms/regional-manager/outstanding" element={<DmsPage Component={RmOutstandingPage} />} />
      <Route path="/dms/regional-manager/outstanding-detail" element={<DmsPage Component={RmOutstandingDetailPage} />} />
      <Route path="/dms/regional-manager/fulfillment" element={<DmsPage Component={RmFulfillmentPage} />} />
      <Route path="/dms/regional-manager/live-tracking" element={<DmsPage Component={LiveTrackingPage} />} />
      <Route path="/dms/regional-manager/team-leaders"  element={<DmsPage Component={RmTeamLeadersPage} />} />
      <Route path="/dms/regional-manager/performance"   element={<DmsPage Component={RmRegionPerformancePage} />} />
      <Route path="/dms/regional-manager/distributors"  element={<DmsPage Component={RmDistributorsPage} />} />
      <Route path="/dms/regional-manager/salespersons"  element={<DmsPage Component={RmSalespersonsPage} />} />
      <Route path="/dms/regional-manager/retailers"     element={<DmsPage Component={RmRetailersPage} />} />
      <Route path="/dms/regional-manager/attendance"    element={<DmsPage Component={AttendancePage} />} />
      <Route path="/dms/regional-manager/products"      element={<DmsPage Component={RmProductsPage} />} />
      <Route path="/dms/owner/attendance"               element={<DmsPage Component={AttendancePage} />} />
      <Route path="/dms/owner/plan-vs-actual"           element={<DmsPage Component={PlanVsActualPage} />} />
      <Route path="/dms/owner/sp-targets"               element={<DmsPage Component={SpTargetsPage} />} />
      <Route path="/dms/team-leader/sp-targets"         element={<DmsPage Component={SpTargetsPage} />} />
      <Route path="/dms/regional-manager/sp-targets"    element={<DmsPage Component={SpTargetsPage} />} />
      <Route path="/dms/hr/attendance" element={<DmsPage Component={HrAttendancePage} />} />
      <Route path="/dms/hr/plan-vs-actual" element={<DmsPage Component={HrPlanActualPage} />} />
      <Route path="/dms/hr/performance" element={<DmsPage Component={HrPerformancePage} />} />
      <Route path="/dms/hr/salary" element={<DmsPage Component={HrSalaryMasterPage} />} />
      <Route path="/dms/attendance-me"                  element={<DmsPage Component={AttendancePage} />} />
      <Route path="/dms/owner/retailer-access"          element={<DmsPage Component={RetailerAccessPage} />} />      <Route path="/dms/salesperson/attendance"         element={<DmsPage Component={AttendancePage} />} />

      {/* Distributor */}
      <Route path="/dms/distributor/browse"           element={<DmsPage Component={DistributorBrowsePage} />} />
      <Route path="/dms/distributor/sale-liters"      element={<DmsPage Component={DistributorSaleLitersPage} />} />
      <Route path="/dms/distributor/my-orders"        element={<DmsPage Component={DistributorOrdersPage} />} />
      <Route path="/dms/distributor/my-orders/:id"    element={<DmsPage Component={DistributorOrderDetailPage} />} />
      <Route path="/dms/distributor/returns"          element={<DmsPage Component={DistributorReturnsPage} />} />
      <Route path="/dms/distributor/stock"            element={<DmsPage Component={DistributorStockPage} />} />
      <Route path="/dms/distributor/price-list"       element={<DmsPage Component={DistributorPriceListPage} />} />
      <Route path="/dms/distributor/ledger"           element={<DmsPage Component={PrimaryLedgerPage} />} />
      <Route path="/dms/retailer/my-transactions"      element={<DmsPage Component={RetailerMyTransactionsPage} />} />
      <Route path="/dms/distributor/retailers"        element={<DmsPage Component={DistRetailersPage} />} />
      <Route path="/dms/distributor/salespersons"     element={<DmsPage Component={DistributorSalespersonsPage} />} />
      <Route path="/dms/distributor/stock-alerts"     element={<DmsPage Component={DistributorStockAlertsPage} />} />
      <Route path="/dms/distributor/retailers/:id"    element={<DmsPage Component={DistRetailerDetailPage} />} />
      <Route path="/dms/distributor/retail-orders"    element={<DmsPage Component={DistSecondaryOrdersPage} />} />
      <Route path="/dms/distributor/retail-orders/:id" element={<DmsPage Component={DistSecondaryOrderDetailPage} />} />
      <Route path="/dms/distributor/sec-ledger"       element={<DmsPage Component={SecondaryLedgerPage} />} />
      <Route path="/dms/distributor/collections"      element={<DmsPage Component={SpCollectionsPage} />} />
      <Route path="/dms/distributor/my-transactions"  element={<DmsPage Component={DistributorMyTransactionsPage} />} />

      {/* Retailer */}
      <Route path="/dms/retailer/browse"        element={<DmsPage Component={RetailerBrowsePage} />} />
      <Route path="/dms/retailer/my-orders"     element={<DmsPage Component={RetailerOrdersPage} />} />
      <Route path="/dms/retailer/my-orders/:id" element={<DmsPage Component={RetailerOrderDetailPage} />} />

      {/* Salesperson */}
      <Route path="/dms/salesperson"                element={<DmsPage Component={DmsDashboardRouter} />} />
      <Route path="/dms/salesperson/distributors"  element={<DmsPage Component={SpDistributorsPage} />} />
      <Route path="/dms/salesperson/retailers"     element={<DmsPage Component={SpRetailersPage} />} />
      <Route path="/dms/salesperson/new-retailer"  element={<DmsPage Component={SpNewRetailerPage} />} />
      <Route path="/dms/salesperson/new-order"     element={<DmsPage Component={SpNewOrderPage} />} />
      <Route path="/dms/salesperson/orders"        element={<DmsPage Component={SpOrdersPage} />} />
      <Route path="/dms/salesperson/sale-liters" element={<DmsPage Component={SpSaleLitersPage} />} />
      <Route path="/dms/salesperson/collections"   element={<DmsPage Component={SpCollectionsPage} />} />
      <Route path="/dms/salesperson/outstanding"   element={<DmsPage Component={SpOutstandingPage} />} />
      <Route path="/dms/salesperson/edit-retailer/:retailerId" element={<DmsPage Component={SpEditRetailerPage} />} />
      <Route path="/dms/salesperson/collect"       element={<DmsPage Component={SpCollectPaymentPage} />} />

      {/* Team Leader */}
      <Route path="/dms/team-leader/distributors" element={<DmsPage Component={TlDistributorsMonitoringPage} />} />
      <Route path="/dms/team-leader/stock-alerts" element={<DmsPage Component={TlStockAlertsPage} />} />
      <Route path="/dms/team-leader/outstanding" element={<DmsPage Component={TlOutstandingPage} />} />
      <Route path="/dms/team-leader/outstanding-detail" element={<DmsPage Component={TlOutstandingDetailPage} />} />

      {/* Super Admin */}
      <Route path="/dms/admin/users" element={<DmsPage Component={SuperAdminUsersPage} />} />

      {/* Print pages (no shell) */}
      <Route path="/dms/print/ebill/:id"          element={<PrintPage Component={PrintEbillPage} />} />
      <Route path="/dms/print/retailer-bill/:id"  element={<PrintPage Component={PrintRetailerBillPage} />} />
      <Route path="/dms/print/challan/:id"        element={<PrintPage Component={PrintChallanPage} />} />
      <Route path="/dms/print/purchase-order/:id" element={<PrintPage Component={PrintPurchaseOrderPage} />} />
      <Route path="/dms/print/document/:id"       element={<PrintPage Component={PrintDocumentPage} />} />

      <Route path="/" element={<Navigate to="/dms" replace />} />
      <Route path="/app/*" element={<Navigate to="/dms" replace />} />
      <Route path="*" element={<Navigate to="/dms" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster richColors position="top-right" />
        <AppUpdatePrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}
